import { useEffect, useState } from "react";
import { fmtDate } from "../lib/date";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import InsuranceSummaryCard from "../components/InsuranceSummaryCard";
import { Prohibit, Warning, WarningOctagon, Wrench, CalendarCheck, SealCheck, Car, GasPump, Waveform, IdentificationCard, DownloadSimple, UserCircle, Truck } from "@phosphor-icons/react";

function dlCsv(filename, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a"); a.href = url; a.download = filename.replace(/[^a-z0-9.-]+/gi, "-"); a.click(); URL.revokeObjectURL(url);
}
function assetCsv(list, detailLabel = "Detail") {
  const rows = [["Asset #", "Name", "Registration", detailLabel]];
  (list || []).forEach((a) => rows.push([a.asset_number, a.name, a.registration || "", a.detail || a.expiry || a.due_date || (a.days_out != null ? `${a.days_out} days out` : "")]));
  return rows;
}

function Metric({ label, value, tone, testid }) {
  const tones = {
    red: "bg-red-600 text-white", amber: "bg-amber-400 text-slate-900",
    orange: "bg-orange-500 text-white", blue: "bg-blue-600 text-white",
    emerald: "bg-emerald-600 text-white", slate: "bg-slate-900 text-white",
  };
  return (
    <div data-testid={testid} className={`rounded-lg p-5 ${tones[tone]}`}>
      <div className="font-display text-4xl font-black leading-none">{value}</div>
      <div className="font-body text-sm mt-2 opacity-90">{label}</div>
    </div>
  );
}

function Panel({ title, Icon, children, count, testid, exportRows, exportName }) {
  const canExport = exportRows && exportRows.length > 1;
  return (
    <div data-testid={testid} className="bg-white border-2 border-slate-300 rounded-lg">
      <div className="flex items-center gap-2 px-4 py-3 border-b-2 border-slate-200">
        <Icon size={20} weight="bold" className="text-slate-700" />
        <h3 className="font-body font-semibold text-slate-800 truncate">{title}</h3>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          {canExport && <button data-testid={`export-${testid}`} onClick={() => dlCsv(`${exportName || title}.csv`, exportRows)} className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors duration-150"><DownloadSimple size={14} weight="bold" />CSV</button>}
          {count != null && <span className="font-mono text-sm font-bold text-slate-500">{count}</span>}
        </div>
      </div>
      <div className="p-2 max-h-80 overflow-y-auto">{children}</div>
    </div>
  );
}

function AssetRow({ a, tone = "slate", detail }) {
  const tones = { red: "text-red-700", amber: "text-amber-700", orange: "text-orange-700", slate: "text-slate-800" };
  return (
    <Link to={`/assets/${a.id}`} data-testid={`dash-asset-${a.id}`}
      className="block px-2 py-2 border-b last:border-0 border-slate-100 hover:bg-slate-50">
      <span className={`font-body text-sm font-semibold ${tones[tone]}`}>{a.asset_number} · {a.name}</span>
      {a.registration && <span className="font-mono text-xs text-slate-500"> · {a.registration}</span>}
      {a.fuel_type && <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase rounded px-1 py-0.5 bg-slate-200 text-slate-700"><GasPump size={10} weight="bold" />{a.fuel_type}</span>}
      {(detail || a.detail) && <div className="font-mono text-xs text-slate-500">{detail || a.detail}</div>}
    </Link>
  );
}

function PolicyComplianceCard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/policies/compliance-summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <Link to="/policy-matrix" data-testid="dash-policy-card" className="block bg-white border-2 border-slate-300 rounded-lg p-4 hover:border-emerald-300 transition-colors">
      <div className="flex items-center gap-2 mb-2"><SealCheck size={18} weight="bold" className="text-emerald-700" /><span className="font-display font-bold text-slate-800">RAMS &amp; Policy acknowledgements</span></div>
      <div className="flex flex-wrap gap-6">
        <div><div className="font-display text-3xl font-black text-amber-600">{s.outstanding}</div><div className="text-xs text-slate-500">Outstanding</div></div>
        <div><div className="font-display text-3xl font-black text-red-600">{s.overdue}</div><div className="text-xs text-slate-500">Overdue</div></div>
        <div><div className="font-display text-3xl font-black text-slate-700">{s.awaiting_clarification}</div><div className="text-xs text-slate-500">Awaiting clarification</div></div>
        <div><div className="font-display text-3xl font-black text-red-700">{s.non_compliant_for_work}</div><div className="text-xs text-slate-500">Non-compliant for work</div></div>
      </div>
    </Link>
  );
}


export function OperationalDashboard() {
  const { hasPerm } = useAuth();
  const [d, setD] = useState(null);
  const [havs, setHavs] = useState(null);
  useEffect(() => { api.get("/dashboard?operators=0").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (hasPerm("supervisor", "administrator")) api.get("/havs/summary").then((r) => setHavs(r.data)).catch(() => {});
  }, [hasPerm]);
  const [allAssets, setAllAssets] = useState([]);
  const [noOps, setNoOps] = useState(null);
  useEffect(() => { api.get("/assets?light=true").then((r) => setAllAssets(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/dashboard?operators=1").then((r) => setNoOps(r.data.no_operators || [])).catch(() => setNoOps([])); }, []);
  if (!d) return (
    <div className="flex flex-col gap-4" data-testid="operational-dashboard">
      <PolicyComplianceCard />
      <p className="text-slate-500">Loading dashboard…</p>
    </div>
  );

  const m = d.maintenance || { due_soon: [], due: [], overdue: [] };
  const road = d.road || {};
  const lic = d.licences || { check_overdue: [], check_due: [], expired: [], expiring: [] };
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  const byAssignee = {};
  allAssets.forEach((a) => { const k = a.assigned_person_name || "Unassigned"; (byAssignee[k] = byAssignee[k] || []).push(a); });
  const assigneeGroups = Object.entries(byAssignee).sort((x, y) => (x[0] === "Unassigned" ? 1 : y[0] === "Unassigned" ? -1 : x[0].localeCompare(y[0])));

  const PersonRow = ({ x, tone = "slate" }) => {
    const tones = { red: "text-red-700", amber: "text-amber-700", slate: "text-slate-800" };
    return (
      <Link to="/people" data-testid={`dash-lic-${x.profile_id}`} className="block px-2 py-2 border-b last:border-0 border-slate-100 hover:bg-slate-50">
        <span className={`font-body text-sm font-semibold ${tones[tone]}`}>{x.name}</span>
        <div className="font-mono text-xs text-slate-500">{x.detail}{x.date ? ` · ${x.date}` : ""}</div>
      </Link>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <InsuranceSummaryCard />
      <PolicyComplianceCard />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric testid="metric-oos" label="Out of Service" value={d.assets.out_of_service.length} tone="red" />
        <Metric testid="metric-open-faults" label="Open Faults" value={d.faults.open_count} tone="slate" />
        <Metric testid="metric-overdue-maint" label="Service Overdue" value={m.overdue.length} tone="orange" />
        <Metric testid="metric-due-soon-maint" label="Service Due Soon" value={m.due_soon.length + m.due.length} tone="amber" />
      </div>

      {/* Hired-In Plant */}
      {d.hire && (d.hire.active > 0 || (d.hire.overdue || []).length > 0 || (d.hire.due_soon || []).length > 0) && (
        <div>
          <h2 className="font-display text-xl font-black text-slate-900 mb-3">Hired-In Plant</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link data-testid="hire-card-active" to="/assets?view=hired" className="rounded-lg p-5 bg-indigo-600 text-white hover:bg-indigo-700 transition-colors duration-150">
              <div className="flex items-center gap-2"><Truck size={20} weight="bold" /><span className="font-display text-4xl font-black leading-none">{d.hire.active}</span></div>
              <div className="font-body text-sm mt-2 opacity-90">On hire now</div>
            </Link>
            <Link data-testid="hire-card-due" to="/assets?view=hired&offhire=due_soon" className="rounded-lg p-5 bg-amber-400 text-slate-900 hover:bg-amber-500 transition-colors duration-150">
              <div className="font-display text-4xl font-black leading-none">{(d.hire.due_soon || []).length}</div>
              <div className="font-body text-sm mt-2 opacity-90">Off-hire due soon</div>
            </Link>
            <Link data-testid="hire-card-overdue" to="/assets?view=hired&offhire=overdue" className="rounded-lg p-5 bg-red-600 text-white hover:bg-red-700 transition-colors duration-150">
              <div className="font-display text-4xl font-black leading-none">{(d.hire.overdue || []).length}</div>
              <div className="font-body text-sm mt-2 opacity-90">Off-hire overdue</div>
            </Link>
          </div>
          {(d.hire.overdue || []).length > 0 && (
            <div className="mt-3">
              <Panel title="Off-hire overdue" Icon={WarningOctagon} count={d.hire.overdue.length} testid="panel-hire-overdue">
                {d.hire.overdue.map((a) => <AssetRow key={a.id} a={a} tone="red" detail={a.detail} />)}
              </Panel>
            </div>
          )}
        </div>
      )}

      {/* Maintenance */}
      <div>
        <h2 className="font-display text-xl font-black text-slate-900 mb-3">Maintenance</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Panel title="Overdue" Icon={Wrench} count={m.overdue.length} testid="panel-maint-overdue" exportRows={assetCsv(m.overdue)} exportName="service-overdue">
            {m.overdue.length ? m.overdue.map((a) => <AssetRow key={a.id} a={a} tone="red" />) : <p className="text-sm text-slate-400 p-2">None overdue.</p>}
          </Panel>
          <Panel title="Due" Icon={CalendarCheck} count={m.due.length} testid="panel-maint-due" exportRows={assetCsv(m.due)} exportName="service-due">
            {m.due.length ? m.due.map((a) => <AssetRow key={a.id} a={a} tone="orange" />) : <p className="text-sm text-slate-400 p-2">None due.</p>}
          </Panel>
          <Panel title="Due Soon" Icon={CalendarCheck} count={m.due_soon.length} testid="panel-maint-due-soon" exportRows={assetCsv(m.due_soon)} exportName="service-due-soon">
            {m.due_soon.length ? m.due_soon.map((a) => <AssetRow key={a.id} a={a} tone="amber" />) : <p className="text-sm text-slate-400 p-2">None approaching.</p>}
          </Panel>
        </div>
      </div>

      {/* Road compliance */}
      <div>
        <h2 className="font-display text-xl font-black text-slate-900 mb-3">Road Compliance (MOT &amp; Tax)</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Panel title="MOT Expired" Icon={WarningOctagon} count={(road.mot_expired || []).length} testid="panel-mot-expired" exportRows={assetCsv(road.mot_expired, "MOT expiry")} exportName="mot-expired">
            {(road.mot_expired || []).length ? road.mot_expired.map((a) => <AssetRow key={a.id} a={a} tone="red" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
          <Panel title="MOT Expiring" Icon={SealCheck} count={(road.mot_expiring || []).length} testid="panel-mot-expiring" exportRows={assetCsv(road.mot_expiring, "MOT expiry")} exportName="mot-expiring">
            {(road.mot_expiring || []).length ? road.mot_expiring.map((a) => <AssetRow key={a.id} a={a} tone="amber" detail={`MOT ${fmtDate(a.expiry)}`} />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
          <Panel title="Tax Untaxed" Icon={Car} count={(road.tax_expired || []).length} testid="panel-tax-expired" exportRows={assetCsv(road.tax_expired)} exportName="tax-untaxed">
            {(road.tax_expired || []).length ? road.tax_expired.map((a) => <AssetRow key={a.id} a={a} tone="red" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
          <Panel title="SORN" Icon={Car} count={(road.tax_sorn || []).length} testid="panel-tax-sorn" exportRows={assetCsv(road.tax_sorn, "Status")} exportName="sorn">
            {(road.tax_sorn || []).length ? road.tax_sorn.map((a) => <AssetRow key={a.id} a={a} tone="slate" detail="Declared off-road (SORN)" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
          <Panel title="Tax Due Soon" Icon={Car} count={(road.tax_due || []).length} testid="panel-tax-due" exportRows={assetCsv(road.tax_due, "Tax due")} exportName="tax-due">
            {(road.tax_due || []).length ? road.tax_due.map((a) => <AssetRow key={a.id} a={a} tone="amber" detail={`Due ${fmtDate(a.due_date)}`} />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
        </div>
      </div>

      {/* Driver licences */}
      <div>
        <h2 className="font-display text-xl font-black text-slate-900 mb-3">Driver Licences</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Panel title="Licence Check Overdue" Icon={WarningOctagon} count={lic.check_overdue.length} testid="panel-lic-check-overdue">
            {lic.check_overdue.length ? lic.check_overdue.map((x) => <PersonRow key={x.profile_id + x.detail} x={x} tone="red" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
          <Panel title="Licence Check Due" Icon={IdentificationCard} count={lic.check_due.length} testid="panel-lic-check-due">
            {lic.check_due.length ? lic.check_due.map((x) => <PersonRow key={x.profile_id + x.detail} x={x} tone="amber" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
          <Panel title="Licence / Category Expired" Icon={WarningOctagon} count={lic.expired.length} testid="panel-lic-expired">
            {lic.expired.length ? lic.expired.map((x) => <PersonRow key={x.profile_id + x.detail} x={x} tone="red" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
          <Panel title="Licence / Category Expiring" Icon={IdentificationCard} count={lic.expiring.length} testid="panel-lic-expiring">
            {lic.expiring.length ? lic.expiring.map((x) => <PersonRow key={x.profile_id + x.detail} x={x} tone="amber" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
          </Panel>
        </div>
      </div>

      {/* Assets & faults */}
      <div>
        <h2 className="font-display text-xl font-black text-slate-900 mb-3">Eligibility &amp; Health</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Panel title="No Qualified Operators" Icon={Prohibit} count={noOps === null ? "…" : (noOps || []).length} testid="panel-no-operators">
            {noOps === null
              ? <p className="text-xs text-slate-400 px-2 py-3">Checking eligibility…</p>
              : (noOps || []).length
              ? noOps.map((a) => <AssetRow key={a.id} a={a} tone="red" detail="Nobody is currently cleared to use this" />)
              : <p className="text-sm text-emerald-700 p-2">All eligibility-required assets have at least one qualified operator.</p>}
          </Panel>
          {havs && (
            <Panel title="HAVS — Today's Exposure" Icon={Waveform} count={havs.people.length} testid="panel-havs">
              {havs.people.length ? havs.people.map((p) => (
                <div key={p.profile_id} data-testid={`havs-person-${p.profile_id}`} className="flex items-center justify-between px-2 py-2 border-b last:border-0 border-slate-100">
                  <span className="font-body text-sm font-semibold text-slate-800">{p.name}</span>
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${p.status === "elv" ? "bg-red-600 text-white" : p.status === "eav" ? "bg-amber-500 text-slate-900" : "bg-slate-200 text-slate-700"}`}>
                    {p.points} pts · {p.status === "elv" ? "over limit" : p.status === "eav" ? "action value" : "ok"}
                  </span>
                </div>
              )) : <p className="text-sm text-slate-400 p-2">No tool exposure logged today.</p>}
            </Panel>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Out of Service" Icon={Prohibit} count={d.assets.out_of_service.length} testid="panel-oos" exportRows={assetCsv(d.assets.out_of_service, "Days out")} exportName="out-of-service">
          {d.assets.out_of_service.length ? d.assets.out_of_service.map((a) => <AssetRow key={a.id} a={a} tone="red" detail={a.days_out != null ? `${a.days_out} days out` : null} />) : <p className="text-sm text-slate-400 p-2">None out of service.</p>}
          {(d.assets.out_of_service_long || []).length > 0 && (
            <>
              <div className="font-body text-xs font-bold uppercase tracking-wide text-slate-400 px-2 mt-3 mb-1">Long-term (over 1 month)</div>
              {d.assets.out_of_service_long.map((a) => <AssetRow key={a.id} a={a} tone="slate" detail={a.days_out != null ? `${a.days_out} days out` : null} />)}
            </>
          )}
        </Panel>
        <Panel title="Restricted / Blocked" Icon={Warning} count={d.assets.restricted.length} testid="panel-restricted" exportRows={assetCsv(d.assets.restricted)} exportName="restricted">
          {d.assets.restricted.length ? d.assets.restricted.map((a) => <AssetRow key={a.id} a={a} tone="amber" />) : <p className="text-sm text-slate-400 p-2">None.</p>}
        </Panel>
        <Panel title="Open Faults" Icon={WarningOctagon} count={d.faults.open_count} testid="panel-faults">
          {d.faults.open.length ? d.faults.open.map((f) => (
            <Link key={f.id} to="/faults" className="block px-2 py-2 border-b last:border-0 border-slate-100 hover:bg-slate-50">
              {(f.asset_number || f.asset_name) && (
                <span className="font-body text-xs font-bold uppercase tracking-wide text-blue-700 block">
                  {f.asset_number ? `#${f.asset_number} ` : ""}{f.asset_name}{f.asset_registration ? ` · ${f.asset_registration}` : ""}
                </span>
              )}
              <span className="font-body text-sm text-slate-800">{f.description}</span>
            </Link>
          )) : <p className="text-sm text-slate-400 p-2">No open faults.</p>}
        </Panel>
        <div className="grid grid-cols-2 gap-4 content-start">
          <Metric testid="metric-training-expired" label="Training Expired" value={d.training.expired} tone="red" />
          <Metric testid="metric-training-expiring" label="Training Expiring" value={d.training.expiring} tone="amber" />
        </div>
      </div>

      {/* Assets by Assignee — "My Team" view */}
      <div>
        <h2 className="font-display text-xl font-black text-slate-900 mb-3">Assets by Assignee</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assigneeGroups.length === 0 && <p className="text-sm text-slate-400">No assets.</p>}
          {assigneeGroups.map(([name, list]) => (
            <Panel key={name} title={name} Icon={UserCircle} count={list.length} testid={`panel-assignee-${slug(name)}`} exportRows={assetCsv(list, "Registration")} exportName={`assets-${slug(name)}`}>
              {list.map((a) => <AssetRow key={a.id} a={a} tone={name === "Unassigned" ? "slate" : "slate"} />)}
            </Panel>
          ))}
        </div>
      </div>
    </div>
  );
}
